import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import DOMPurify from 'dompurify';
import config from "../config";
import { calculateRouteDistance } from './routeUtils';
import useTranslation from './useTranslation';
import authService from '../services/auth';
import LocalIcon from './LocalIcon';

const Icon = ({ name, className, style }) => (
  <LocalIcon icon={name} className={className} style={style} />
);

const RouteBox = ({ routes: initialRoutes }) => {
  const { t } = useTranslation();
  const [routes, setRoutes] = useState(initialRoutes);
  const [activeIndex, setActiveIndex] = useState(0);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);
  const [touchStart, setTouchStart] = useState(null);
  const [touchEnd, setTouchEnd] = useState(null);
  const sliderRef = useRef(null);
  const navigate = useNavigate();
  const [selectedLang] = useState(localStorage.getItem('language') || config.languages.default);

  const truncateText = (text, maxLength = 160) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // Reset dello stato quando cambiano le props
  useEffect(() => {
    setRoutes(initialRoutes);
    setActiveIndex(0);
  }, [initialRoutes]);

  // Calcolo delle distanze ottimizzato
  useEffect(() => {
    const fetchDistances = async () => {
      // Filtra solo i percorsi che necessitano del calcolo
      const routesNeedingDistances = initialRoutes.filter(route => {
        // Se la distanza è già presente e valida, non calcolarla
        if (route.field_distanza?.[0]?.value && route.field_distanza[0].value !== '') {
          return false;
        }
        // Verifica che il percorso abbia almeno 2 POI
        return route.field_poi?.length > 1;
      });

      if (routesNeedingDistances.length === 0) {
        return; // Nessun calcolo necessario
      }

      // Raggruppa gli ID POI necessari
      const allPoiIds = [...new Set(
        routesNeedingDistances.flatMap(route => 
          route.field_poi.map(poi => poi.target_id)
        )
      )];

      // Carica i POI necessari
      const poisPromises = allPoiIds.map(poiId =>
        authService.get(config.API_ENDPOINTS.getNode(poiId, selectedLang))
      );
      
      const poisResponses = await Promise.all(poisPromises);
      const poisMap = Object.fromEntries(
        poisResponses.map(response => [response.nid[0].value, response])
      );

      // Calcola le distanze solo per i percorsi che ne hanno bisogno
      const updatedRoutes = await Promise.all(
        initialRoutes.map(async (route) => {
          // Se la distanza è già presente, mantienila
          if (route.field_distanza?.[0]?.value && route.field_distanza[0].value !== '') {
            return route;
          }

          // Calcola la distanza solo se necessario
          if (route.field_poi?.length > 1) {
            try {
              const routePois = route.field_poi
                .map(poi => poisMap[poi.target_id])
                .filter(Boolean);

              // Verifica che tutti i POI abbiano le coordinate
              const hasValidCoordinates = routePois.every(poi => 
                poi.field_geolocalizzazione?.[0]?.lat && 
                poi.field_geolocalizzazione?.[0]?.lng
              );

              if (hasValidCoordinates) {
                const distance = await calculateRouteDistance(routePois);
                if (distance?.totalDistance) {
                  return {
                    ...route,
                    field_distanza: [{ value: distance.totalDistance }]
                  };
                }
              }
            } catch (error) {
              console.error(`Error calculating distance for route ${route.nid[0].value}:`, error);
            }
          }
          
          return route;
        })
      );

      setRoutes(updatedRoutes);
    };

    fetchDistances();
  }, [initialRoutes, selectedLang]);

  useEffect(() => {
    handleScroll();
  }, [routes]);

  const handleScroll = () => {
    if (!sliderRef.current) return;
    
    const slider = sliderRef.current;
    const scrollPosition = slider.scrollLeft;
    const cardWidth = slider.offsetWidth;
    const newIndex = Math.round(scrollPosition / cardWidth);
    
    setActiveIndex(newIndex);
    setCanScrollLeft(scrollPosition > 0);
    setCanScrollRight(
      scrollPosition < slider.scrollWidth - slider.clientWidth - 1 &&
      routes.length > 1
    );
  };

  const handleTouchStart = (e) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && activeIndex < routes.length - 1) {
      scrollToIndex(activeIndex + 1);
    }
    if (isRightSwipe && activeIndex > 0) {
      scrollToIndex(activeIndex - 1);
    }
  };

  useEffect(() => {
    const slider = sliderRef.current;
    if (!slider) return;

    slider.addEventListener('touchstart', handleTouchStart);
    slider.addEventListener('touchmove', handleTouchMove);
    slider.addEventListener('touchend', handleTouchEnd);
    slider.addEventListener('scroll', handleScroll);
    window.addEventListener('resize', handleScroll);

    return () => {
      slider.removeEventListener('touchstart', handleTouchStart);
      slider.removeEventListener('touchmove', handleTouchMove);
      slider.removeEventListener('touchend', handleTouchEnd);
      slider.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [activeIndex]);

  const scrollToIndex = (index) => {
    if (!sliderRef.current || index < 0 || index >= routes.length) return;
    
    const slider = sliderRef.current;
    const cardWidth = slider.offsetWidth;
    const scrollPosition = cardWidth * index;
    
    slider.scrollTo({
      left: scrollPosition,
      behavior: 'smooth'
    });
  };

  const sanitizeConfig = {
    ALLOWED_TAGS: [], 
    KEEP_CONTENT: true 
  };

  if (!routes || routes.length === 0) return null;

  return (
    <div className="mb-4">
      <h3 className="mb-3">
        {routes.length === 1 ? t('parteDelPercorso') : t('deiPercorsi')}
      </h3>
      
      <div className="position-relative">
        {canScrollLeft && (
          <button
            className="btn bg-white btn-custom-left border rounded-circle btn-scroll opacity-75 shadow-sm position-absolute start-0 top-50 translate-middle-y d-flex align-items-center justify-content-center"
            onClick={() => scrollToIndex(activeIndex - 1)}
            aria-label="{t('precedente')} /"
            style={{
              zIndex: 10,
              width: '32px',
              height: '32px',
            }}
          >
            <Icon name="chevron_left" style={{ fontSize: '16px' }} />
          </button>
        )}
        
        {canScrollRight && (
          <button
            className="btn bg-white border btn-custom-right rounded-circle btn-scroll opacity-75 shadow-sm position-absolute end-0 top-50 translate-middle-y d-flex align-items-center justify-content-center"
            onClick={() => scrollToIndex(activeIndex + 1)}
            aria-label="{t('successivo')}"
            style={{
              zIndex: 10,
              width: '32px',
              height: '32px',
            }}
          >
            <Icon name="chevron_right" style={{ fontSize: '16px' }} />
          </button>
        )}

        <div 
          ref={sliderRef}
          className="routes-slider"
          style={{
            display: 'flex',
            overflowX: 'auto',
            scrollSnapType: 'x mandatory',
            WebkitOverflowScrolling: 'touch',
            margin: '0 -1rem',
            position: 'relative',
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            touchAction: 'pan-x pan-y pinch-zoom'
          }}
        >
          {routes.map((route, index) => (
            <div 
              key={route.nid[0].value} 
              style={{ 
                minWidth: '100%',
                scrollSnapAlign: 'start',
                padding: '0 1rem'
              }}
            >
              <div 
                className="route-card position-relative shadow-sm rounded overflow-hidden"
                style={{ 
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column'
                }}
              >
                <div 
                  className="position-relative" 
                  style={{ 
                    flex: 1,
                    minHeight: '200px'
                  }}
                >
                  {route.field_immagine_percorso && route.field_immagine_percorso[0] && (
                    <img
                      src={route.field_immagine_percorso[0].url}
                      alt={route.field_immagine_percorso[0].alt || route.title[0].value}
                      className="w-100 h-100 position-absolute"
                      style={{ 
                        objectFit: 'cover',
                        top: 0,
                        left: 0
                      }}
                    />
                  )}
                  <div 
                    className="position-absolute w-100 h-100" 
                    style={{ 
                      background: 'linear-gradient(to bottom, rgba(0,0,0,0.2), rgba(0,0,0,0.7))',
                      top: 0,
                      left: 0
                    }}
                  />
                  
                  <div className="position-relative p-4 text-white text-start" style={{ zIndex: 0 }}>
                    <div className="d-flex justify-content-between mb-3">
                      {route.field_tempi_medi_di_percorrenza?.[0]?.processed && (
                        <span className="text-white small d-flex align-items-center">
                          <Icon name="schedule" className="text-white align-bottom me-1" style={{ fontSize: '0.875rem' }} />
                          {DOMPurify.sanitize(route.field_tempi_medi_di_percorrenza[0].processed, sanitizeConfig)}
                        </span>
                      )}
                      <span className="text-white small d-flex align-items-center">
                        <Icon name="signpost" className="text-white align-bottom me-1" style={{ fontSize: '0.875rem' }} />
                        {route.field_poi?.length || 0} {t('tappe')}
                      </span>
                      {route.field_distanza?.[0]?.value && (
                        <span className="text-white small d-flex align-items-center">
                          <Icon name="near_me" className="text-white align-bottom me-1" style={{ fontSize: '0.875rem' }} />
                          {route.field_distanza[0].value}
                        </span>
                      )}
                    </div>

                    <h4 className="fw-bold mb-2" style={{ fontSize: '1.25rem' }}>
                      {route.title[0].value}
                    </h4>

                    {route.field_descrizione_breve?.[0]?.value && (
                      <div 
                        className="text-white mb-3"
                        style={{ fontSize: '0.875rem' }}
                      >
                        {DOMPurify.sanitize(truncateText(route.field_descrizione_breve[0].value), sanitizeConfig)}
                      </div>
                    )}

                    <div className="text-start">
                      <button
                        onClick={() => navigate(`/routes/${route.nid[0].value}`)}
                        className="btn bg-white border rounded-pill py-1 px-3 d-flex align-items-center shadow-sm"
                        style={{ fontSize: '0.875rem' }}
                      >
                        {t('scopriPercorso')}
                        <Icon name="chevron_right" className="ms-1" style={{ fontSize: '1.1rem' }} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {routes.length > 1 && (
          <div className="d-flex justify-content-center mt-3" style={{ gap: '8px' }}>
            {routes.map((_, index) => (
              <button
                key={index}
                onClick={() => scrollToIndex(index)}
                className="btn p-0 border-0"
                style={{
                  width: index === activeIndex ? '24px' : '8px',
                  height: '8px',
                  borderRadius: '4px',
                  backgroundColor: index === activeIndex ? '#6c757d' : '#D1D5DB',
                  transition: 'all 0.3s ease'
                }}
                aria-label={`Slide ${index + 1}`}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default RouteBox;